import {
  Dispatch,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useReducer,
} from 'react'
import { ChartType, CustomChartState, DataType, Selectable } from './types'

type Action =
  | {
      type: 'SET_CHART_TYPE'
      payload: { chartType: ChartType }
    }
  | {
      type: 'SET_DATA_TYPE'
      payload: { dataType: DataType }
    }
  | {
      type: 'SET_COLOR_SCHEME'
      payload: { colorScheme: string }
    }
  | {
      type: 'SET_ITEMS'
      payload: { items: Selectable[] }
    }
  | {
      type: 'SET_CURRENT'
      payload: { current: number }
    }
  | {
      type: 'SET_NAME'
      payload: { name: string }
    }
  | {
      type: 'SET_DESCRIPTION'
      payload: { description: string }
    }
  | {
      type: 'SET_SHOW_MODAL'
      payload: { showModal: boolean }
    }
  | {
      type: 'SET_TAG_CATEGORY'
      payload: { tagCategory: Selectable | null }
    }
  | {
      type: 'RESET'
    }

export const DEFAULT_STATE: CustomChartState = {
  current: 0,
  name: '',
  description: '',
  showModal: false,
  selectedTagCategory: null,
}

const CustomChartContext = createContext({
  state: DEFAULT_STATE,
  dispatch: () => {},
} as {
  state: CustomChartState
  dispatch: Dispatch<Action>
})

const reducer = (state: CustomChartState, action: Action): CustomChartState => {
  const newState = { ...state }

  switch (action.type) {
    case 'SET_CHART_TYPE':
      newState.chartType = action.payload.chartType
      break
    case 'SET_DATA_TYPE':
      newState.dataType = action.payload.dataType
      newState.items = []
      newState.selectedTagCategory = null
      break
    case 'SET_COLOR_SCHEME':
      newState.colorScheme = action.payload.colorScheme
      break
    case 'SET_ITEMS':
      newState.items = action.payload.items
      break
    case 'SET_CURRENT':
      newState.current = action.payload.current
      break
    case 'SET_NAME':
      newState.name = action.payload.name
      break
    case 'SET_DESCRIPTION':
      newState.description = action.payload.description
      break
    case 'SET_TAG_CATEGORY':
      newState.selectedTagCategory = action.payload.tagCategory
      newState.items = []
      break
    case 'RESET':
      return DEFAULT_STATE
    case 'SET_SHOW_MODAL':
      newState.showModal = action.payload.showModal
      break
    default:
      break
  }

  return {
    ...newState,
  }
}

export function CustomChartWrapper({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch]
  )

  return (
    <CustomChartContext.Provider value={contextValue}>
      {children}
    </CustomChartContext.Provider>
  )
}

export function useCustomChartContext() {
  return useContext(CustomChartContext)
}
